import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { learning } from "@/api";
import http from "@/api";
import { PILLARS, TIER_META } from "@/constants";
import {
  ArrowLeft, Plus, Trash2, Pencil, X, Save, GraduationCap,
  Upload, Image as ImageIcon, FileText, Pin, Eye,
} from "lucide-react";
import Modal from "@/components/Modal";

const TYPE_OPTIONS = [
  { key: "article", label: "Artículo (Markdown)" },
  { key: "video", label: "Video" },
  { key: "infographic", label: "Infografía" },
  { key: "document", label: "Documento (PDF)" },
  { key: "link", label: "Enlace" },
  { key: "capsule", label: "Cápsula" },
];

const EMPTY = {
  title: "", type: "article", pillar: "", tier_level: "all",
  url: "", description: "", duration_minutes: "", active: true,
  body_markdown: "", cover_url: "", file_name: "", cover_file_name: "",
  order: 0, is_pinned: false,
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const absMedia = (u) => (u && u.startsWith("/api/") ? `${API_BASE}${u}` : u);

// Nombre del archivo subido; para archivos anteriores sin file_name se muestra el de la URL.
const uploadedFileLabel = (fileName, url) => (
  url && url.startsWith("/api/media/") ? (fileName || url.split("/").pop()) : ""
);

function UploadedFile({ name, url, testId }) {
  const label = uploadedFileLabel(name, url);
  if (!label) return null;
  return (
    <div className="text-[11px] text-emerald-700 mt-1 truncate" title={label} data-testid={testId}>
      ✓ Archivo disponible: <strong>{label}</strong>
    </div>
  );
}

export function HeroVideoCard() {
  const [videos, setVideos] = useState({
    hero_video_url: "", hero_poster_url: "", hero_video_file_name: "", hero_poster_file_name: "",
  });
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState("");
  useEffect(() => {
    http.get("/admin/platform-videos").then((r) => setVideos(r.data)).catch(() => {});
  }, []);
  const uploadFile = async (kind, file) => {
    if (!file) return;
    setUploading(kind); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/learning/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // A fresh upload clears the "not available" warning at once; saving recomputes it.
      setVideos((v) => kind === "video"
        ? { ...v, hero_video_url: data.url, hero_video_file_name: data.file_name, hero_video_available: null }
        : { ...v, hero_poster_url: data.url, hero_poster_file_name: data.file_name });
      setMsg(`Archivo subido: ${data.file_name}. Da clic en Guardar para aplicarlo al Home.`);
    } catch (e) {
      setMsg("Error al subir: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading("");
    }
  };
  const save = async () => {
    setMsg("");
    try {
      const { data } = await http.put("/admin/platform-videos", videos);
      setVideos(data);
      setMsg("Guardado. El Home ya usa este video.");
    } catch (e) {
      setMsg("Error: " + (e.response?.data?.detail || e.message));
    }
  };
  return (
    <div className="sketch-border bg-white p-5 mb-6" data-testid="hero-video-card">
      <div className="font-display text-xl">Video del Home (dashboard público)</div>
      <p className="text-xs text-[var(--ink-soft)] mt-1 mb-3">
        Cambia el video del inicio sin tocar código: pega la URL del archivo (mp4) y su portada. Se guarda en la base de datos.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">URL del video (.mp4)</label>
          <input value={videos.hero_video_url} onChange={(e) => setVideos({ ...videos, hero_video_url: e.target.value, hero_video_file_name: "", hero_video_available: null })}
            className="input-field mt-1" data-testid="hero-video-url-input" />
          <UploadedFile name={videos.hero_video_file_name} url={videos.hero_video_url} testId="hero-video-file-name" />
          {videos.hero_video_available === false && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mt-2"
              data-testid="hero-video-unavailable">
              El video del Home no está disponible. Vuelve a subirlo.
            </div>
          )}
          <label className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--lala-blue)] cursor-pointer hover:underline">
            <Upload size={12} /> {uploading === "video" ? "Subiendo…" : "o sube el archivo de video"}
            <input type="file" accept=".mp4,.webm,.mov" className="hidden" disabled={!!uploading}
              data-testid="hero-video-file-input"
              onChange={(e) => uploadFile("video", e.target.files?.[0])} />
          </label>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">URL de la portada (imagen)</label>
          <input value={videos.hero_poster_url} onChange={(e) => setVideos({ ...videos, hero_poster_url: e.target.value, hero_poster_file_name: "" })}
            className="input-field mt-1" data-testid="hero-poster-url-input" />
          <UploadedFile name={videos.hero_poster_file_name} url={videos.hero_poster_url} testId="hero-poster-file-name" />
          <label className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--lala-blue)] cursor-pointer hover:underline">
            <Upload size={12} /> {uploading === "poster" ? "Subiendo…" : "o sube la imagen de portada"}
            <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" disabled={!!uploading}
              data-testid="hero-poster-file-input"
              onChange={(e) => uploadFile("poster", e.target.files?.[0])} />
          </label>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={save} className="btn-primary text-sm" data-testid="hero-video-save">Guardar video del Home</button>
        {msg && <span className="text-xs font-semibold text-emerald-700" data-testid="hero-video-msg">{msg}</span>}
      </div>
    </div>
  );
}

export default function LearningAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const data = await learning.resources.listAdmin({ active_only: false });
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new");
    setDraft(EMPTY);
    setErr(""); setMsg("");
  };
  const openEdit = (r) => {
    setEditing(r.id);
    setDraft({
      title: r.title || "",
      type: r.type || "article",
      pillar: r.pillar || "",
      tier_level: r.tier_level || "all",
      url: r.url || "",
      description: r.description || "",
      duration_minutes: r.duration_minutes ?? "",
      active: r.active !== false,
      body_markdown: r.body_markdown || "",
      cover_url: r.cover_url || "",
      file_name: r.file_name || "",
      cover_file_name: r.cover_file_name || "",
      order: r.order ?? 0,
      is_pinned: r.is_pinned === true,
    });
    setErr(""); setMsg("");
  };

  const close = () => { setEditing(null); setDraft(EMPTY); setErr(""); setMsg(""); };

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const body = {
        title: draft.title.trim(),
        type: draft.type,
        pillar: draft.pillar || null,
        tier_level: draft.tier_level,
        url: draft.url.trim() || null,
        description: draft.description.trim() || null,
        duration_minutes: draft.duration_minutes ? Number(draft.duration_minutes) : null,
        active: draft.active,
        body_markdown: draft.body_markdown?.trim() || null,
        cover_url: draft.cover_url || null,
        file_name: draft.file_name,
        cover_file_name: draft.cover_file_name,
        order: Number(draft.order) || 0,
        is_pinned: !!draft.is_pinned,
      };
      if (editing === "new") {
        await learning.resources.create(body);
        setMsg("Recurso creado.");
      } else {
        await learning.resources.update(editing, body);
        setMsg("Recurso actualizado.");
      }
      await load();
      setTimeout(close, 600);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar "${r.title}"? Esta acción no se puede deshacer.`)) return;
    await learning.resources.remove(r.id);
    await load();
  };

  const uploadFile = async (fileObj, field) => {
    if (!fileObj) return;
    if (fileObj.size > 100 * 1024 * 1024) {
      setErr("Archivo demasiado grande. Máximo 100 MB.");
      return;
    }
    const form = new FormData();
    form.append("file", fileObj);
    try {
      const res = await http.post("/admin/learning/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const nameField = field === "cover_url" ? "cover_file_name" : "file_name";
      setDraft((prev) => ({ ...prev, [field]: res.data.url, [nameField]: res.data.file_name }));
      setMsg(`Archivo subido: ${res.data.file_name} (${(res.data.size / 1024 / 1024).toFixed(1)} MB)`);
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="learning-admin-page">
      <Link to="/admin/configuracion" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Volver a configuración
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">centro de aprendizaje</div>
          <h1 className="font-display text-4xl flex items-center gap-2"><GraduationCap size={32} /> Recursos</h1>
          <p className="text-[var(--ink-soft)] mt-1">{rows.length} recursos · curados por pilar y nivel para cada proveedor.</p>
        </div>
        <button onClick={openNew} className="btn-primary text-sm" data-testid="new-resource-btn">
          <Plus size={16} /> Nuevo recurso
        </button>
      </div>

      <HeroVideoCard />

      <div className="sketch-border bg-[var(--cream-soft)] overflow-hidden" data-testid="resources-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cream-deep)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Título</th>
              <th className="px-4 py-3 font-semibold">Tipo</th>
              <th className="px-4 py-3 font-semibold">Pilar</th>
              <th className="px-4 py-3 font-semibold">Nivel</th>
              <th className="px-4 py-3 font-semibold">Activo</th>
              <th className="px-4 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--line)] hover:bg-[var(--cream)]" data-testid={`resource-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {r.cover_url ? (
                      <img src={absMedia(r.cover_url)} alt="" className="w-9 h-9 object-cover rounded-md border border-[var(--line)]" />
                    ) : (
                      <div className="w-9 h-9 rounded-md bg-[var(--cream-deep)] flex items-center justify-center">
                        {r.type === "article" ? <FileText size={14} /> : <ImageIcon size={14} className="text-[var(--ink-soft)]" />}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold flex items-center gap-1.5">
                        {r.is_pinned && <Pin size={10} className="text-[var(--lala-blue)]" />}
                        {r.title}
                      </div>
                      {r.description && <div className="text-[10px] text-[var(--ink-soft)] truncate max-w-xs">{r.description}</div>}
                      {r.file_name && (
                        <div className="text-[10px] text-emerald-700 truncate max-w-xs" title={r.file_name} data-testid={`resource-file-name-${r.id}`}>
                          Archivo: {r.file_name}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-[var(--ink-soft)]">{r.type}</td>
                <td className="px-4 py-3 text-[var(--ink-soft)]">{PILLARS.find((p) => p.key === r.pillar)?.label || "—"}</td>
                <td className="px-4 py-3 text-[var(--ink-soft)]">{r.tier_level === "all" ? "Todos" : TIER_META[r.tier_level]?.label || r.tier_level}</td>
                <td className="px-4 py-3">{r.active ? "✓" : "✗"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(r)} className="sketch-pill bg-[var(--cream)] px-2.5 py-1 text-xs font-semibold text-[var(--lala-blue)] mr-1" data-testid={`edit-resource-${r.id}`}>
                    <Pencil size={12} className="inline mr-1" /> Editar
                  </button>
                  <button onClick={() => remove(r)} className="sketch-pill bg-[var(--cream)] px-2.5 py-1 text-xs font-semibold text-red-700 border-red-600" style={{ boxShadow: "3px 3px 0 0 #B91C1C" }} data-testid={`delete-resource-${r.id}`}>
                    <Trash2 size={12} className="inline mr-1" /> Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-[var(--ink-soft)]">Sin recursos. Agrega el primero.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal testId="resource-editor-modal" align="center">
          <div className="bg-[var(--cream)] sketch-border max-w-xl w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-hand text-xl text-[var(--lala-blue)]">{editing === "new" ? "nuevo recurso" : "editar recurso"}</div>
                <h2 className="font-display text-2xl">{draft.title || "Sin título"}</h2>
              </div>
              <button onClick={close} className="text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="close-editor"><X size={20} /></button>
            </div>

            <div className="grid gap-3">
              <Field label="Título *">
                <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="input-field" required data-testid="resource-title" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Formato">
                  <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                    className="input-field" data-testid="resource-type">
                    {TYPE_OPTIONS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="Duración (min, opcional)">
                  <input type="number" value={draft.duration_minutes} onChange={(e) => setDraft({ ...draft, duration_minutes: e.target.value })}
                    className="input-field" data-testid="resource-duration" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Pilar (opcional)">
                  <select value={draft.pillar} onChange={(e) => setDraft({ ...draft, pillar: e.target.value })}
                    className="input-field" data-testid="resource-pillar">
                    <option value="">Transversal</option>
                    {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </Field>
                <Field label="Nivel objetivo">
                  <select value={draft.tier_level} onChange={(e) => setDraft({ ...draft, tier_level: e.target.value })}
                    className="input-field" data-testid="resource-tier">
                    <option value="all">Todos los niveles</option>
                    {Object.entries(TIER_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="URL externa (YouTube · Vimeo · PDF · imagen · link)">
                <input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value, file_name: "" })}
                  className="input-field" placeholder="https://…" data-testid="resource-url" />
                <div className="text-[10px] text-[var(--ink-soft)] mt-1">o sube un archivo propio abajo</div>
              </Field>

              {/* File upload */}
              {["video", "document", "infographic"].includes(draft.type) && (
                <Field label={`Subir archivo propio (${draft.type === "video" ? "mp4/webm/mov" : draft.type === "document" ? "PDF" : "imagen"}) · máx 100 MB`}>
                  <label className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer hover:bg-white"
                    data-testid="upload-file-btn">
                    <Upload size={12} /> Elegir archivo
                    <input type="file" hidden
                      accept={draft.type === "video" ? "video/*" : draft.type === "document" ? "application/pdf" : "image/*"}
                      onChange={(e) => uploadFile(e.target.files?.[0], "url")}
                      data-testid="upload-file-input" />
                  </label>
                  <UploadedFile name={draft.file_name} url={draft.url} testId="upload-file-current" />
                </Field>
              )}

              {/* Article body (markdown) */}
              {["article", "capsule"].includes(draft.type) && (
                <Field label="Cuerpo del artículo (Markdown)">
                  <textarea value={draft.body_markdown}
                    onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })}
                    rows={10} className="input-field font-mono text-sm"
                    placeholder={"# Título\n\nEscribe tu artículo aquí. Puedes usar **negritas**, listas, *cursivas*."}
                    data-testid="resource-body-markdown" />
                  <div className="text-[10px] text-[var(--ink-soft)] mt-1">
                    Soporta Markdown: <code># H1</code>, <code>## H2</code>, <code>**bold**</code>, <code>*ital*</code>, listas con <code>-</code>, enlaces <code>[texto](url)</code>.
                  </div>
                </Field>
              )}

              {/* Cover image */}
              <Field label="Imagen de portada (opcional)">
                <div className="flex items-center gap-3">
                  <label className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer hover:bg-white"
                    data-testid="upload-cover-btn">
                    <ImageIcon size={12} /> {draft.cover_url ? "Cambiar portada" : "Subir portada"}
                    <input type="file" hidden accept="image/*"
                      onChange={(e) => uploadFile(e.target.files?.[0], "cover_url")}
                      data-testid="upload-cover-input" />
                  </label>
                  {draft.cover_url && (
                    <>
                      <img src={absMedia(draft.cover_url)} alt="portada"
                        className="w-12 h-12 object-cover rounded-md border border-[var(--line)]"
                        data-testid="cover-preview" />
                      <button type="button" onClick={() => setDraft({ ...draft, cover_url: "", cover_file_name: "" })}
                        className="text-xs text-red-700 hover:underline">quitar</button>
                    </>
                  )}
                </div>
                <UploadedFile name={draft.cover_file_name} url={draft.cover_url} testId="upload-cover-current" />
              </Field>

              <Field label="Descripción">
                <textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={3} className="input-field" data-testid="resource-description" />
              </Field>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Orden (0 = arriba)">
                  <input type="number" value={draft.order}
                    onChange={(e) => setDraft({ ...draft, order: e.target.value })}
                    className="input-field" data-testid="resource-order" />
                </Field>
                <div className="col-span-2 flex items-end gap-4">
                  <label className="flex items-center gap-2 text-sm" data-testid="resource-pinned-label">
                    <input type="checkbox" checked={draft.is_pinned}
                      onChange={(e) => setDraft({ ...draft, is_pinned: e.target.checked })} data-testid="resource-pinned" />
                    <Pin size={14} /> Fijar arriba
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={draft.active}
                      onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="resource-active" />
                    Activo (visible para proveedores)
                  </label>
                </div>
              </div>
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mt-3" data-testid="resource-error">{err}</div>}
            {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 mt-3" data-testid="resource-success">{msg}</div>}

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={close} className="btn-ghost text-sm">Cancelar</button>
              <button onClick={save} disabled={busy || !draft.title.trim()} className="btn-primary text-sm" data-testid="save-resource-btn">
                <Save size={14} /> {busy ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
